import React from "react";
import { SbEditableBlok, sbEditable } from "@storyblok/storyblok-editable";
import clsx from "clsx";
import { DEVICE_TYPE_MOBILE, DEVICE_TYPE_TABLET } from "constants/viewports";
import { screenContext } from "context/ScreenContext";
import Image from 'next/image';

type StoryblokImage = {
  id?: number | null;
  alt: string | null;
  name?: string;
  focus?: string | null;
  title?: string | null;
  filename: string | null;
  copyright?: string | null;
  fieldtype?: "asset";
  source?: "";
  meta_data?: {};
  is_external_url?: boolean;
};

export interface ImageV2Props {
  blok: Partial<SbEditableBlok> & {
    desktopImage: StoryblokImage;
    mobileImage?: StoryblokImage;
    height?: number | string;
    width?: number | string;
    imageLink?: {
      cached_url: string;
      url?: string;
      linktype?: string;
      id?: string;
      fieldtype?: string;
      prep?: boolean;
    };
    isImageWithLink?: boolean;
    shouldPrioritizeLoading?: boolean;
    shouldZoomToFullHeight?: boolean;
  };
  className?: string;
  key?: string;
  isShouldZoomToFullHeight?: boolean
}

export function ImageV2({
  blok: {
    desktopImage,
    mobileImage,
    height,
    width,
    imageLink,
    isImageWithLink,
    isLinkOpenInNewTab,
    shouldPrioritizeLoading = false,
    shouldZoomToFullHeight,
    ...blokData
  },
  className,
  key,
  isShouldZoomToFullHeight,
}: ImageV2Props) {
  
  const { currentDevice } = React.useContext(screenContext);
  const isMobile = currentDevice === DEVICE_TYPE_MOBILE || currentDevice === DEVICE_TYPE_TABLET;
  const selectedImage = isMobile && mobileImage?.filename ? mobileImage : desktopImage;

  const imageDynamicClassName = clsx({
    "w-full": !width,
    "object-cover h-full": shouldZoomToFullHeight || isShouldZoomToFullHeight,
    "cursor-pointer": isImageWithLink,
  });

  const interLink = imageLink?.cached_url.includes("/en")
    ? imageLink.cached_url.slice(3)
    : imageLink?.cached_url || "/";

  const renderImage = (src: string | null, alt: string | null) => (
    <Image
      src={src || ""}
      alt={alt || ""}
      className={imageDynamicClassName}
      loading={shouldPrioritizeLoading ? "eager": "lazy"}
      priority={shouldPrioritizeLoading}
      {...(height && width && !Number.isNaN(+height) && !Number.isNaN(+width)
        ? { height: +height, width: +width }
        : { layout: "fill" })}
    />
  );
  const renderLink = (src: string | null, alt: string | null) => 
    isLinkOpenInNewTab ? (
      <a href={imageLink?.linktype === "story" ? interLink : imageLink?.url} aria-label={alt || ""} target="_blank">
        {renderImage(src, alt)}
      </a>
    ) : (
      <a href={imageLink?.linktype === "story" ? interLink : imageLink?.url} aria-label={alt || ""}>
        {renderImage(src, alt)}
      </a>
    );

  return (
    <div
      className={`bg-transparent flex w-full justify-center h-full relative ${className}`}
      {...sbEditable(blokData as SbEditableBlok)}
      key={key}
    >
      {isImageWithLink && imageLink ? (
        renderLink(selectedImage.filename, selectedImage.alt)
      ) : (
        renderImage(selectedImage.filename, selectedImage.alt)
      )}
    </div>
  );
}

export default ImageV2;
